import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { Badge, Button, EmptyState, KeyValue, Section, Spinner } from '@zt/ui';
import { api, newIdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, rateLabel } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { FxFetchResult, FxOverview, FxRecomputeResult } from '../lib/types';

/**
 * Döviz kurları (F7-11, ADR-116): TCMB günlük gösterge kurları, çekme durumu ve eski kur uyarısı, kurdan türetilmiş
 * fiyat listeleri. Kur uydurulmaz; alınamazsa son geçerli kur ve tarihi gösterilir.
 */

const RATE_TYPES = ['forex_buying', 'forex_selling', 'banknote_buying', 'banknote_selling'] as const;
type RateKey = 'forexBuying' | 'forexSelling' | 'banknoteBuying' | 'banknoteSelling';
const RATE_KEY: Record<(typeof RATE_TYPES)[number], RateKey> = {
  forex_buying: 'forexBuying',
  forex_selling: 'forexSelling',
  banknote_buying: 'banknoteBuying',
  banknote_selling: 'banknoteSelling',
};

export function useFxOverview(enabled = true) {
  return useQuery({
    queryKey: ['fx'],
    queryFn: () => api.get<FxOverview>('/v1/admin/fx'),
    enabled,
  });
}

/** "1 EUR = 48,6120 TL" (birim 100 ise "100 JPY = …"). */
export function usePairLabel() {
  const t = useT();
  const { locale } = useI18n();
  return (p: { currency: string; rate: string; unit: number }) =>
    t('admin.fx.pair', { unit: p.unit, currency: p.currency, rate: rateLabel(p.rate, locale) });
}

/** YYYY-AA-GG → yerel tarih (saat dilimi kaydırması olmadan). */
export function useBulletinDate() {
  const { formatDate } = useI18n();
  return (ymd: string) => formatDate(`${ymd}T12:00:00Z`, false);
}

export function FxPage() {
  const t = useT();
  const { locale, formatDate } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('pricing.price.write');
  const q = useFxOverview();
  const pair = usePairLabel();
  const day = useBulletinDate();
  const refresh = useApiMutation(
    () => api.post<FxFetchResult>('/v1/admin/fx/refresh', undefined, { idempotencyKey: newIdempotencyKey() }),
    {
      invalidate: [['fx'], ['price-lists']],
      success: (r) =>
        r.result === 'updated' || r.result === 'unchanged'
          ? t(`admin.fx.refreshResult.${r.result}`, { date: day(r.bulletinDate), count: r.recomputed })
          : r.result === 'failed'
            ? t('admin.fx.refreshResult.failed', { error: r.error })
            : t(`admin.fx.refreshResult.${r.result}`),
    },
  );
  const recompute = useApiMutation(
    (id: string) =>
      api.post<FxRecomputeResult>(`/v1/admin/price-lists/${id}/fx/recompute`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      invalidate: [['fx'], ['price-lists']],
      success: (r) => t('admin.fx.recomputed', { count: r.changed }),
    },
  );
  const o = q.data;
  const st = o?.status;
  return (
    <PageFrame helpKey="page.fx">
      <Section
        title={t('admin.fx.statusTitle')}
        description={t('admin.fx.sourceLead')}
        action={
          canWrite && (
            <span className="inline-flex items-center gap-1">
              <Button size="sm" loading={refresh.isPending} onClick={() => refresh.mutate(undefined)}>
                <RefreshCw className="h-4 w-4" aria-hidden />
                {t('admin.fx.refresh')}
              </Button>
              <HelpTip k="fx.refresh" />
            </span>
          )
        }
      >
        {q.isLoading || !o || !st ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="space-y-4">
            {st.stale && (
              <p role="status" className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
                {t(`admin.fx.stale.${st.staleReason ?? 'no_data'}`, {
                  date: st.lastBulletinDate ? day(st.lastBulletinDate) : '—',
                  count: st.failCount,
                })}
              </p>
            )}
            <KeyValue
              items={[
                { label: t('admin.fx.source'), value: t('admin.fx.sourceName') },
                {
                  label: t('admin.fx.bulletinLabel'),
                  value: o.bulletin
                    ? [
                        t('admin.fx.bulletin', { date: day(o.bulletin.date) }),
                        o.bulletin.bulletinNo
                          ? t('admin.fx.bulletinNo', { no: o.bulletin.bulletinNo })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : t('admin.fx.noBulletin'),
                },
                {
                  label: t('admin.fx.lastSuccess'),
                  value: st.lastSuccessAt ? formatDate(st.lastSuccessAt, true) : '—',
                },
                {
                  label: t('admin.fx.nextAttempt'),
                  value: st.nextAttemptAt
                    ? formatDate(st.nextAttemptAt, true)
                    : t('admin.fx.nextAttemptNone'),
                },
                ...(st.lastError ? [{ label: t('admin.fx.lastError'), value: st.lastError }] : []),
              ]}
            />
          </div>
        )}
      </Section>

      <Section
        title={t('admin.fx.ratesTitle')}
        description={t('admin.fx.ratesLead')}
        action={<HelpTip k="fx.rates" />}
      >
        {o?.rates.length ? (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[40rem] text-sm">
              <caption className="sr-only">{t('admin.fx.ratesTitle')}</caption>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.fx.currency')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.fx.unit')}</th>
                  {RATE_TYPES.map((r) => (
                    <th key={r} className="px-3 py-2 text-end font-medium">
                      {t(`admin.fx.rateTypes.${r}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {o.rates.map((r) => (
                  <tr key={r.currency}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="font-medium">{r.currency}</span>
                        {r.inUse && <Badge tone="pine">{t('admin.fx.inUse')}</Badge>}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{r.unit}</td>
                    {RATE_TYPES.map((k) => {
                      const v = r[RATE_KEY[k]];
                      return (
                        <td key={k} className="px-3 py-2 text-end tabular-nums">
                          {v ? (
                            rateLabel(v, locale)
                          ) : (
                            <span className="text-ink-3">{t('admin.fx.notPublished')}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !q.isLoading && <EmptyState title={t('admin.fx.noRates')} description={t('admin.fx.noRatesLead')} />
        )}
      </Section>

      <Section
        title={t('admin.fx.derivedTitle')}
        description={
          <>
            {t('admin.fx.derivedLead')}{' '}
            <Link to="/fiyatlar" className="underline underline-offset-2">
              {t('admin.fx.toPricing')}
            </Link>
          </>
        }
        action={<HelpTip k="fx.derived" />}
      >
        {o?.derivedLists.length ? (
          <ul className="divide-y divide-line rounded-md border border-line">
            {o.derivedLists.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {l.name}{' '}
                    <span className="text-ink-3">
                      · {l.storeName} · {l.currency}
                    </span>
                  </p>
                  <p className="text-sm text-ink-2">
                    {t('admin.fx.derivedFrom', {
                      base: l.baseListName ?? '—',
                      currency: l.baseCurrency ?? '—',
                    })}{' '}
                    ·{' '}
                    {t('admin.fx.rule', {
                      rateType: t(`admin.fx.rateTypes.${l.fx.rateType}`),
                      markup: bpsLabel(l.fx.markupBps),
                      ending: t(`admin.pricing.endings.${l.fx.ending}`),
                    })}
                  </p>
                  <p className="text-sm text-ink-3">
                    {l.applied
                      ? t('admin.fx.applied', {
                          date: day(l.applied.bulletinDate),
                          pair: [l.applied.from, l.applied.to]
                            .filter((s) => s.currency !== 'TRY')
                            .map(pair)
                            .join(' · '),
                          count: l.applied.priced,
                        })
                      : t('admin.fx.notApplied')}
                  </p>
                </div>
                {l.behind && <Badge tone="amber">{t('admin.fx.behind')}</Badge>}
                <Badge tone={l.fx.auto ? 'moss' : 'slate'}>
                  {l.fx.auto ? t('admin.fx.auto') : t('admin.fx.manualOnly')}
                </Badge>
                {canWrite && (
                  <span className="inline-flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={recompute.isPending && recompute.variables === l.id}
                      onClick={() => recompute.mutate(l.id)}
                    >
                      {t('admin.fx.recompute')}
                    </Button>
                    <HelpTip k="fx.recompute" />
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          !q.isLoading && (
            <EmptyState title={t('admin.fx.noDerived')} description={t('admin.fx.noDerivedLead')} />
          )
        )}
      </Section>
    </PageFrame>
  );
}
